import { useTreatmentsWithConfig } from '@splitsoftware/splitio-react';

import {
  DEFAULT_FEATURE_FLAG_TREATMENT_ENABLED_VALUE,
  FeatureFlagAttributes,
  type FeatureFlagName,
} from '#/shared/types/FeatureFlag';

/**
 * Hook for managing feature flags with Split.io (Medworld-Approval-Web)
 * We currently don't support user targeting as we don't have logged-in users/unique ids
 * @param flagName - Feature flag name to evaluate
 * @param attributes - Optional attributes for attribute based targeting rules
 * @returns Object containing isEnabled, treatment, isReady and getTreatmentWithConfig states
 */
export const useFeatureFlag = (flagName: FeatureFlagName, attributes?: FeatureFlagAttributes) => {
  const { treatments, isReady } = useTreatmentsWithConfig({ names: [flagName] as FeatureFlagName[], attributes });

  /**
   * @returns The treatment value with config (treatment: 'on', 'off', 'control', config: null)
   */
  const getTreatmentWithConfig = () => {
    if (!isReady) {
      return { treatment: 'control', config: null };
    }

    return treatments[flagName] || { treatment: 'control', config: null };
  };

  const treatment = getTreatmentWithConfig().treatment;

  /**
   * @returns boolean indicating if the flag is enabled (treatment === DEFAULT_FEATURE_FLAG_TREATMENT_ENABLED_VALUE)
   */
  const isEnabled = treatment === DEFAULT_FEATURE_FLAG_TREATMENT_ENABLED_VALUE;

  return {
    isEnabled,
    isReady,
    treatment,
    getTreatmentWithConfig,
  };
};
